import { PageController } from '@client/components/_core/layout/PageController';
import { ComponentPreview } from '@client/components/_ui/display/ComponentPreview';
import { User } from '@client/components/_ui/display/User';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/reserve-adjustments',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Movements',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ReserveAdjustments />
        </PageController>
    );
}

type QueryParams = RouterInputs['reserve']['listReserveMovements'];

const columnHelper = createColumnHelper<
    RouterOutputs['reserve']['listReserveMovements']['rows'][number],
    QueryParams['orderBy']
>();

function ReserveAdjustments() {
    const { reserveId } = Route.useParams();
    const { formatDate, formatCurrency } = useFormatter();

    const [input, setInput] = useState<QueryParams>({
        where: {
            reserveId,
        },
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { createdAt: 'asc' },
    });

    const { data, isPending, error } = trpc.reserve.listReserveMovements.useQuery({
        ...input,
        where: {
            ...input.where,
            reserveId,
        },
    });

    const tableProps = usePrismaTable({
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        rowKey: 'id',
        queryParams: input,
        onParamsChange(params) {
            setInput({
                ...params,
                where: {
                    ...params.where,
                    reserveId,
                },
            });
        },
        // A descriptive key: the claim page's own table may hold `q` underneath this overlay (CUR-1341).
        urlKey: 'movements',
        queryError: error,
        pagination: {
            position: 'bottom',
        },
        // `reserveId` is the fixed scope and the table has no user filters.
        emptyState: { hasActiveFilters: false, noRecordsTitle: 'No reserve movements yet' },
        columns: [
            columnHelper.accessor('createdAt', {
                id: 'createdAt',
                header: 'Date created',
                sortable: 'createdAt',
                cell: ({ row }) => formatDate(row.createdAt, 'dd MMM yyyy'),
            }),
            columnHelper.accessor((row) => row.CreatedBy?.firstName, {
                id: 'createdBy',
                header: 'Created by',
                sortable: 'CreatedBy.firstName',
                cell: ({ row }) => (
                    <User
                        userId={row.CreatedBy?.userId}
                        photoUrl={row.CreatedBy?.photoUrl}
                        name={row.CreatedBy?.fullName}
                    />
                ),
            }),
            columnHelper.accessor((row) => row.TransactionType?.name, {
                id: 'transactionType',
                header: 'Transaction type',
                sortable: 'TransactionType.name',
                cell: ({ row }) => row.TransactionType?.name,
            }),
            columnHelper.accessor('description', {
                id: 'description',
                header: 'Description',
                cell: ({ row }) => <ComponentPreview>{row.description}</ComponentPreview>,
                className: 'max-w-xs',
            }),
            columnHelper.accessor('amount', {
                id: 'amount',
                header: 'Amount',
                cell: ({ row }) => (
                    <span
                        className={
                            row.TransactionType?.transactionNature === 'RESERVE'
                                ? 'text-blue-600'
                                : row.TransactionType?.transactionNature === 'RESERVE_INCREASE'
                                  ? 'text-green-600'
                                  : 'text-red-600'
                        }
                    >
                        {row.TransactionType.transactionNature === 'RESERVE' ||
                        row.TransactionType.transactionNature === 'RESERVE_INCREASE'
                            ? '+'
                            : '-'}
                        {formatCurrency(row.amount)}
                    </span>
                ),
            }),
        ],
    });

    return (
        <>
            <Outlet />
            <PrismaTable
                {...tableProps}
                className="[&_.ant-table-cell]:align-top"
                // onRow={({ TransactionType }) => ({
                //     // className: TransactionType?.transactionNature === 'RESERVE' ? 'opacity-50' : '',
                // })}
            />
        </>
    );
}
